<template>
  <div class="py-5 md:px-5">
    <Loading v-if="loading" />

    <template v-else>
      <PageHeader class="max-w-7xl" :title="customer.name" :back-step="true" /> 

      <div class="grid grid-cols-4 gap-6 mx-auto max-w-7xl">
        <!-- Details -->
        <div>
          <div class="card">
            <div class="card-header">
              Details
            </div>
            <div class="grid gap-1 p-6">
              <div class="font-medium">
                {{ customer.name }}
              </div>
              <div class="italic">
                {{ customer.company }}
              </div>
              <div>
                {{ customer.email }}
              </div>
              <div>
                {{ customer.phone }}
              </div>
              <div class="mt-4">
                <router-link :to="{ name: 'CustomerEdit', params: { id: $route.params.id } }" class="btn btn-secondary btn-sm">
                  Edit
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Bookings -->
        <div class="col-span-3">
          <div class="card">
            <div class="card-header">
              Booking History
            </div>
            <div class="grid grid-cols-2 px-6 py-6">
              <DatePicker  color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.created_at">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex justify-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      class="form-input prepend"
                    />
                    <div class="flex items-center justify-center flex-none w-10 text-gray-500 bg-gray-100 border-t border-b border-gray-300">
                      &rarr;
                    </div>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      class="form-input append"
                    />
                  </div>
                </template>
              </DatePicker>
            </div>
            <div class="border-t">
              <div v-if="bookings.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
                <div class="mb-2 text-lg font-bold">
                  No bookings found.
                </div>
                <div class="text-gray-500">
                  There were no bookings placed by this customer in the chosen date range.
                </div>
              </div>
              <div class="divide-y" v-else>
                <div v-for="booking in bookings.data" :key="booking.id" class="block text-gray-800 transition duration-150 ease-in-out hover:no-underline hover:bg-gray-50 focus:outline-none focus:bg-gray-50">
                  <div class="px-4 py-4 space-y-3 md:space-y-0 md:grid md:gap-3 md:grid-cols-4 sm:px-6">
                    <div>
                      <div class="font-medium" >
                        <span v-if="booking.date">
                          {{ booking.date | moment('ddd DD MMM') }}
                        </span>
                        <span v-else>
                          No Date
                        </span>
                        <span>
                          {{ booking.time || 'Any Time' }} 
                        </span>
                      </div>
                    </div>
                    <div>
                      <div class="flex">
                        <badge class="py-1" :class="parseBookingStateClass(booking.status)">
                          <fa :icon="['fas', 'circle']" size="xs" class="mr-1 transform -translate-y-px" />
                          {{ booking.status | capitalize }}
                        </badge>
                      </div>
                    </div>
                    <div class="relative col-span-2 md:col-span-1">
                      <div class="flex items-center space-x-1">
                        <badge v-if="booking.origins.length" class="bg-gray-100">
                          <div class="px-2 py-1 bg-gray-200" slot="prefix">{{ booking.origins.length }}</div> 
                          {{ booking.origins.length | pluralize('Pickup') }}
                        </badge>
                        <badge v-if="booking.destinations.length" class="bg-gray-100">
                          <div class="px-2 py-1 bg-gray-200" slot="prefix">{{ booking.destinations.length }}</div>
                          {{ booking.destinations.length | pluralize(['Delivery', 'Deliveries']) }}
                        </badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Pagination -->
            <Pagination class="border-t rounded-b-lg" :total="bookings.meta.total" :per-page="bookings.meta.per_page" :current-page="bookings.meta.current_page" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { sub } from 'date-fns'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
  name: 'CustomerView',

  metaInfo: {
    title: 'View Customer'
  },

  components: {
    DatePicker,
  },

  created () {
    this.getCustomer()
  },

  data () {
    return {
      loading: true,
      customer: {},
      bookings: {},
      filter: {
        created_at: { start: sub(new Date(), { years: 1 }), end: new Date() }
      }
    }
  },

  methods: {
    async getCustomer () {
      try {
        const { data } = await this.$api.get(`/account/${this.$route.params.account}/customers/${this.$route.params.id}`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            filter: this.filter
          }
        })
        this.customer = data.customer
        this.bookings = data.bookings
        this.loading = false
      } catch (error) {
        this.$toasted.error('There was a problem fetching this customer.')
        console.error(error)
      }
    },

    parseBookingStateClass (status) {
      if (status === 'pending') { return 'text-gray-500 bg-gray-100' }
      else if (status === 'draft') { return 'text-yellow-500 bg-yellow-100' }
      else if (status === 'approved') { return 'text-blue-500 bg-blue-100' }
      else if (status === 'cancelled') { return 'text-red-500 bg-red-100' }
      else if (status === 'complete') { return 'text-emerald-500 bg-emerald-100' }
    }
  }
}
</script>
